// Full inventory of the admin dashboard, grouped into categories, for the
// Site Map (src/components/settings/AdminSiteMap.tsx). Plain data, not JSX,
// so the large amount of label/description text stays out of component code.
//
// `navigate.path` is a stack of nested sub-tab values, outermost first --
// see src/lib/adminNav.ts for how each level gets consumed:
//   - path[0] for a top-level tab whose sub-tab state lives directly in
//     AdminDashboard.tsx (settings -> settingsSubTab) or in the six listener
//     components (DriverManagement, FleetManagement, RosterPage,
//     AdminECardHub, IvrGeneralSettings, PaymentTypeManagement).
//   - path[1] for a further level AdminDashboard also owns directly
//     (fleet-defaults -> fleetDefaultsSubTab), or that one of the six
//     listener components owns itself (fleet risk view, ecard tags/
//     reporting sub-tabs, ivr category, payment-type sub-tab).
// Nodes with no `navigate` aren't wired up -- mostly things nested inside a
// modal that needs a specific record picked first (e.g. DriverEditModal's
// tabs), which isn't a navigable "place."
import type { LucideIcon } from "lucide-react";
import { Car, Users, CalendarDays, Landmark, FileSpreadsheet, BadgeDollarSign, Wallet, Phone, Settings, Gauge, SlidersHorizontal, ShieldAlert } from "lucide-react";
import type { AdminTab } from "./adminNav";

export interface SiteMapNode {
  label: string;
  description: string;
  navigate?: { tab: AdminTab; path?: string[] };
  children?: SiteMapNode[];
}

export interface SiteMapCategory {
  label: string;
  description: string;
  icon: LucideIcon;
  items: SiteMapNode[];
}

export const ADMIN_SITE_MAP: SiteMapCategory[] = [
  {
    label: "Vehicles & Fleet",
    description: "The vehicle fleet itself -- inventory, edits, inspections, fuel cards, and TaxiCaller sync.",
    icon: Car,
    items: [
      {
        label: "Fleet Management",
        description: "Vehicle list, add/edit vehicles, inspection history, fuel cards, and TaxiCaller sync.",
        navigate: { tab: "fleet" },
        children: [
          {
            label: "Fleet Risk Snapshot",
            description: "Fleet-wide risk dashboard -- counts of unsafe, missing-data, due-soon, stale, overdue and upcoming vehicles.",
            navigate: { tab: "fleet", path: ["risk", "overview"] },
            children: [
              { label: "Unsafe Vehicles", description: "Vehicles flagged unsafe on their last inspection.", navigate: { tab: "fleet", path: ["risk", "unsafe"] } },
              { label: "Missing Data", description: "Vehicles missing required fields.", navigate: { tab: "fleet", path: ["risk", "missing"] } },
              { label: "Due Soon", description: "COF/Rego due within 30 days.", navigate: { tab: "fleet", path: ["risk", "duesoon"] } },
              { label: "Stale Data", description: "Vehicles whose KM or inspection data is out of date.", navigate: { tab: "fleet", path: ["risk", "stale"] } },
              { label: "Upcoming Schedule", description: "Service/COF/Rego/RUC/tyre schedule, overdue and upcoming.", navigate: { tab: "fleet", path: ["risk", "upcoming"] } },
            ],
          },
          { label: "Fleet Vehicles", description: "Searchable, filterable vehicle table with export.", navigate: { tab: "fleet", path: ["vehicles"] } },
          {
            label: "Maintenance",
            description: "Log maintenance work against a vehicle, and review maintenance history/reports.",
            navigate: { tab: "fleet", path: ["maintenance"] },
            children: [
              { label: "Log Work", description: "Form to record a maintenance job against a vehicle/system/work-type." },
              { label: "Records & Reports", description: "Filterable maintenance history table." },
            ],
          },
          {
            label: "Add New Vehicle",
            description: "Manual entry form or bulk spreadsheet import.",
            navigate: { tab: "fleet", path: ["add"] },
            children: [
              { label: "Manual Entry", description: "Fleet #, plate, make/model, type, status, capacity, equipment." },
              { label: "Bulk Upload", description: "Spreadsheet import of multiple vehicles at once." },
            ],
          },
          { label: "Inspection History", description: "Past vehicle check submissions across the fleet.", navigate: { tab: "fleet", path: ["history"] } },
          { label: "Fuel Cards", description: "Fuel card inventory and vehicle assignment.", navigate: { tab: "fleet", path: ["fuelcards"] } },
          { label: "EFTPOS Machines", description: "EFTPOS terminal inventory and vehicle assignment -- assigning a machine sets the vehicle's EFT ID for matching.", navigate: { tab: "fleet", path: ["eftpos"] } },
          { label: "New Vehicle Setup", description: "Onboarding checklist for a newly added vehicle.", navigate: { tab: "fleet", path: ["setup"] } },
          { label: "TaxiCaller Sync (Fleet)", description: "Vehicle field mismatches between DMS and TaxiCaller, with push/apply/dismiss.", navigate: { tab: "fleet", path: ["taxicaller"] } },
        ],
      },
    ],
  },
  {
    label: "Fleet Capacity & Pulse",
    description: "Live scheduling and utilisation analytics across the fleet.",
    icon: Gauge,
    items: [
      {
        label: "Fleet Capacity",
        description: "Day/week slot-sheet view of every vehicle's schedule, by vehicle type, plus STS and driver breaks.",
        navigate: { tab: "fleetcapacity" },
        children: [
          { label: "Day view", description: "Per-vehicle-type slot grid for a single day, plus an STS lane and a Breaks tab." },
          { label: "Week view", description: "One tab per vehicle type, plus STS, summarised across the week." },
        ],
      },
      {
        label: "Fleet Pulse",
        description: "Trip and utilisation analytics across the fleet -- overview, Total Mobility, per-vehicle/driver breakdowns, hoist demand, TM client coverage, data gaps, and a TaxiCaller setup check.",
        navigate: { tab: "fleetpulse" },
      },
    ],
  },
  {
    label: "Fleet Settings",
    description: "Fleet-wide defaults, vehicle types, and the lists used when logging maintenance.",
    icon: SlidersHorizontal,
    items: [
      {
        label: "Fleet Defaults (Settings)",
        description: "Interval/threshold and traffic-light settings for vehicle checks, COF, Rego, maintenance, and RUC.",
        navigate: { tab: "settings", path: ["fleet-defaults", "fleet-setup"] },
        children: [
          { label: "Fleet Setup", description: "Defaults applied when creating or importing a vehicle.", navigate: { tab: "settings", path: ["fleet-defaults", "fleet-setup"] } },
          { label: "Fleet Checks", description: "Vehicle-check interval/shift threshold, lockout message, the configurable checklist itself, and how often admin inspections and camera checks are due.", navigate: { tab: "settings", path: ["fleet-defaults", "fleet-checks"] } },
          { label: "COF", description: "Certificate of Fitness expiry warning and traffic-light thresholds.", navigate: { tab: "settings", path: ["fleet-defaults", "cof"] } },
          { label: "Rego", description: "Registration expiry warning and traffic-light thresholds.", navigate: { tab: "settings", path: ["fleet-defaults", "rego"] } },
          { label: "Maintenance", description: "Service/maintenance warning km and traffic-light thresholds.", navigate: { tab: "settings", path: ["fleet-defaults", "maintenance"] } },
          { label: "RUC", description: "Road User Charges warning km and traffic-light thresholds.", navigate: { tab: "settings", path: ["fleet-defaults", "ruc"] } },
          { label: "Traffic Lights", description: "General green/amber/red km thresholds.", navigate: { tab: "settings", path: ["fleet-defaults", "traffic-lights"] } },
        ],
      },
      {
        label: "Vehicle Types (Settings)",
        description: "Vehicle-type capacity settings, the vehicle-type compatibility matrix, and per-slot colours on the Fleet Capacity sheet.",
        navigate: { tab: "settings", path: ["vehicle-capacity"] },
      },
      {
        label: "Maintenance Lists (Settings)",
        description: "The vehicle-systems and work-types lists used when logging a maintenance record.",
        navigate: { tab: "settings", path: ["maintenance-lists"] },
      },
    ],
  },
  {
    label: "Drivers",
    description: "Driver records, IDs, key fobs, and driver-type configuration.",
    icon: Users,
    items: [
      {
        label: "Driver Management",
        description: "Driver list with compliance colour-coding, add driver, key fobs, and TaxiCaller sync.",
        navigate: { tab: "drivers" },
        children: [
          { label: "Drivers", description: "Searchable/filterable driver list; edit opens a 7-tab modal (details, admin, IDs, payments, deductions, roster, links) for the selected driver." },
          { label: "Add New Driver", description: "Form to create a new driver record." },
          { label: "Key Fobs", description: "Key fob inventory and assignment to drivers." },
          { label: "TaxiCaller Sync (Drivers)", description: "DL/ID mismatches vs TaxiCaller, and drivers TaxiCaller couldn't auto-match." },
        ],
      },
      {
        label: "Driver Types (Settings)",
        description: "The list of driver types (e.g. Standard, Contractor) used across payments and reporting.",
        navigate: { tab: "settings", path: ["driver-types"] },
      },
    ],
  },
  {
    label: "Roster",
    description: "Shift scheduling, driver positions, groups, holidays, and work-time compliance rules.",
    icon: CalendarDays,
    items: [
      {
        label: "Roster",
        description: "The shift calendar, demand forecasting, list view, night-before allocation messages, swap requests, and time-off requests.",
        navigate: { tab: "roster" },
        children: [
          { label: "Calendar", description: "Main Day/Week/Month roster grid with drag-drop shift creation and publishing." },
          { label: "Demand", description: "Historical/forecast job-demand trends vs actual completed-job performance." },
          { label: "List View", description: "Flat table of all shifts in the selected range." },
          { label: "Allocation", description: "Night-before driver allocation summary text, grouped by schedule group." },
          { label: "Swaps", description: "Admin review of driver shift-swap requests.", navigate: { tab: "roster", path: ["swaps"] } },
          { label: "Time Off", description: "Admin review/approval of driver time-off requests.", navigate: { tab: "roster", path: ["timeoff"] } },
          { label: "Blocked Dates for Leave", description: "Block leave requests for a day or period (e.g. Christmas / New Year), for everyone or chosen teams/driver types, with your own message to drivers.", navigate: { tab: "roster", path: ["timeoff"] } },
        ],
      },
      { label: "Roster Positions (Settings)", description: "Shift/position types, including vehicle-type requirements and the dispatcher-role flag.", navigate: { tab: "settings", path: ["roster-positions"] } },
      { label: "Roster Groups (Settings)", description: "Driver/roster groups used for allocation messaging and scheduling.", navigate: { tab: "settings", path: ["roster-groups"] } },
      { label: "Roster Holidays (Settings)", description: "Public holiday dates the roster and payroll logic account for.", navigate: { tab: "settings", path: ["roster-holidays"] } },
      { label: "Work Time Rules (Settings)", description: "Max shift length and rest-break rules that hard-block roster violations.", navigate: { tab: "settings", path: ["work-time-rules"] } },
    ],
  },
  {
    label: "Accounts & Jobs",
    description: "CRM accounts, job/account matching, manual job entry, and the daily action-required review queue.",
    icon: Landmark,
    items: [
      { label: "CRM Accounts", description: "Search, view and edit CRM account records.", navigate: { tab: "crm" } },
      { label: "Match Hub", description: "Match unlinked jobs to CRM accounts.", navigate: { tab: "matchhub" } },
      { label: "Job Input", description: "Manually enter a TaxiCaller job and record its payment.", navigate: { tab: "jobinput" } },
      {
        label: "Closed Check",
        description: "Daily review queue: action-required alerts, open/reconciled jobs by driver week, and new/updated TaxiCaller accounts to review.",
        navigate: { tab: "closedcheck" },
        children: [
          { label: "Action Required", description: "Fleet issues, service/COF/maintenance bookings, and roster alerts, all in one place." },
          { label: "Open Jobs", description: "Drivers who still have open (unpaid) jobs." },
          { label: "Closed Not Reconciled", description: "Closed driver weeks still needing payment reconciliation." },
          { label: "Fully Reconciled", description: "Driver weeks completed and balanced." },
          { label: "No Jobs This Week", description: "Active report drivers with no jobs this week." },
          { label: "New Accounts", description: "TaxiCaller account codes not yet in the CRM." },
          { label: "Account Updates", description: "Accounts whose name/phone/status differs from TaxiCaller." },
        ],
      },
    ],
  },
  {
    label: "Reporting & Exports",
    description: "Data exports, uploaded reports, timesheets, weekly summaries, and payout reporting.",
    icon: FileSpreadsheet,
    items: [
      { label: "Export Builder", description: "Build a column template, preview, run anomaly detection, and export job data -- with invoice runs, sort rules, address alias/town rules.", navigate: { tab: "exportbuilder" } },
      { label: "Reports", description: "Upload a report file for parsing/import, with an upload history log.", navigate: { tab: "reports" } },
      { label: "Timesheets", description: "Driver timesheet review table with Excel export.", navigate: { tab: "timesheets" } },
      { label: "Weekly Summary", description: "Weekly payout workspace -- driver rows, adjustments, rate resolution.", navigate: { tab: "summary" } },
      { label: "Payout Report", description: "Payout report table with date range and export.", navigate: { tab: "payout" } },
      { label: "Deductions Report", description: "Every driver charge and deduction by driver and date range -- original amount, each payment, balance still owed. Reverse, correct or cancel with a reason; export CSV.", navigate: { tab: "payout", path: ["deductions"] } },
    ],
  },
  {
    label: "eCard & Loyalty",
    description: "Prepaid eCard inventory, promotions, tagging, prize draws, and eCard-specific reporting.",
    icon: BadgeDollarSign,
    items: [
      {
        label: "eCard Hub",
        description: "Card inventory, CRM auto-match, promotions, tags, prize draws, and eCard reporting.",
        navigate: { tab: "ecard" },
        children: [
          { label: "Cards", description: "Card inventory and balance management." },
          { label: "Auto-Match", description: "Wizard matching eCards to CRM accounts." },
          { label: "Promotions", description: "Promo/top-up bonus configuration." },
          { label: "Tags", description: "Create/edit tags and assign them to cards, promotions, or historical top-ups.", navigate: { tab: "ecard", path: ["tags", "manage"] } },
          { label: "Draws", description: "Prize draw management." },
          {
            label: "Reporting (eCard)",
            description: "EFTPOS transactions, sales/top-up totals, and totals broken down by tag.",
            navigate: { tab: "ecard", path: ["reporting", "eftpos"] },
          },
        ],
      },
      { label: "eCard Defaults (Settings)", description: "Default values used when a new eCard promo is created.", navigate: { tab: "settings", path: ["ecard-defaults"] } },
    ],
  },
  {
    label: "Payments & Billing",
    description: "Payment types, payment summary/rate-rule building, commission/GST settings, and driver bonus tiers.",
    icon: Wallet,
    items: [
      {
        label: "Payment Types (Settings)",
        description: "View/edit/merge existing payment types, add a new one, and map payout columns to payment types.",
        navigate: { tab: "settings", path: ["payment-types", "current"] },
        children: [
          { label: "Current Payment Types", description: "View, edit and merge existing payment types.", navigate: { tab: "settings", path: ["payment-types", "current"] } },
          { label: "New Payment Type", description: "Add a type with Standard/Contractor/Leased rates.", navigate: { tab: "settings", path: ["payment-types", "new"] } },
          { label: "Payment Type Mapping", description: "Map payout columns to payment types.", navigate: { tab: "settings", path: ["payment-types", "mapping"] } },
        ],
      },
      { label: "Payment Builder (Settings)", description: "Drag-and-drop payment summary/rate-rule config builder.", navigate: { tab: "settings", path: ["payment-builder"] } },
      { label: "Payment Settings (Settings)", description: "Insurance excess, commission %, TM hoist fee, and deduction categories.", navigate: { tab: "settings", path: ["payment-settings"] } },
      { label: "Bonus Tiers (Settings)", description: "Driver bonus tier thresholds.", navigate: { tab: "settings", path: ["bonus-tiers"] } },
    ],
  },
  {
    label: "IVR",
    description: "The phone booking line's voice AI -- company identity, greetings, service area, booking rules, and integration credentials.",
    icon: Phone,
    items: [
      {
        label: "IVR Settings",
        description: "Everything governing the automated phone booking line.",
        navigate: { tab: "settings", path: ["ivr", "general"] },
        children: [
          { label: "General", description: "Company identity.", navigate: { tab: "settings", path: ["ivr", "general"] } },
          { label: "Greetings", description: "Caller greeting scripts.", navigate: { tab: "settings", path: ["ivr", "greetings"] } },
          { label: "Service Area", description: "Geofence and radius.", navigate: { tab: "settings", path: ["ivr", "service-area"] } },
          { label: "Address Recognition", description: "Speech hints and address trimming.", navigate: { tab: "settings", path: ["ivr", "address"] } },
          { label: "Booking Rules", description: "Timing and advance-booking limits.", navigate: { tab: "settings", path: ["ivr", "booking-rules"] } },
          { label: "TaxiCaller (IVR)", description: "Dispatch API credentials.", navigate: { tab: "settings", path: ["ivr", "taxicaller"] } },
          { label: "OpenAI", description: "Realtime voice model configuration.", navigate: { tab: "settings", path: ["ivr", "openai"] } },
          { label: "Twilio & Call Routing", description: "Telephony and bailout routing.", navigate: { tab: "settings", path: ["ivr", "twilio"] } },
          { label: "API Keys", description: "Keys for the IVR developer.", navigate: { tab: "settings", path: ["ivr", "api-keys"] } },
        ],
      },
    ],
  },
  {
    label: "Insurance & Claims",
    description: "Incident recording, insurer claim tracking, and the driver excess fund.",
    icon: ShieldAlert,
    items: [
      { label: "Incidents", description: "Log incidents and review fault classification / excess impact.", navigate: { tab: "insurance", path: ["incidents"] } },
      { label: "Claims", description: "Full and Section 2 claims, insurer submission status.", navigate: { tab: "insurance", path: ["claims"] } },
      { label: "Driver History", description: "Every incident a driver was on or responsible for -- protection-only claims included -- with excess share, paid and still owed. Export CSV.", navigate: { tab: "insurance", path: ["driverhistory"] } },
      { label: "Excess Fund", description: "Per-driver start date, tenure, running balance, backfill of worked weeks, and the No Claim Rebate (when due, when paid).", navigate: { tab: "insurance", path: ["excessfund"] } },
      { label: "Vehicle Values", description: "Current insured value per vehicle.", navigate: { tab: "insurance", path: ["vehiclevalues"] } },
      { label: "Insurance Settings", description: "Graduated excess amounts, weekly contribution, tenure windows, and driver-portal report field requirements.", navigate: { tab: "settings", path: ["insurance-settings"] } },
    ],
  },
  {
    label: "General Settings",
    description: "App-wide settings that don't fit elsewhere.",
    icon: Settings,
    items: [
      { label: "Branding (Settings)", description: "Company name, logo, tab icon, colours and login tagline (what the login screen looks like once your code is entered).", navigate: { tab: "settings", path: ["branding"] } },
    ],
  },
];
